import { Correct } from '../../components/Correct'
import type { BenchmarkView } from '../../types/outreach'
import type { Reconciliation, ReconciliationRow } from '../../types/onboarding'

// YOUR WORKBOOK, RECONCILED. Two plain tables for the owner to read (PriorityContacts.cs):
//   1. the reconciliation — what matched, what is new, who is a target, where your record and ours
//      disagree, and whom your removed tab sets aside that we still hold (offered, never done);
//   2. the benchmark — your A / B / C against Scout's, with both sides' reasons in words (PL-125).
// Nothing here is computed: the API reconciled and compared; this reads the numbers back in your words.

export const VERDICT_WORDS: Record<string, string> = {
  matched: 'on file',
  new: 'new',
  target: 'target',
  conflict: 'conflict',
  'set-aside': 'set aside?',
  'not-on-file': 'not on file',
}

export const BAND_WORDS: Record<string, string> = {
  a: 'Band A', b: 'Band B', c: 'Band C', d: 'Band D', reactivation: 'Reactivation',
  'recently-lost': 'Recently lost', removed: 'Removed', merged: 'Merged away', none: 'No band',
}

export function bandWord(key: string | null | undefined): string {
  return key ? (BAND_WORDS[key] ?? key) : 'No band'
}

export function ReconciliationPanel({ r }: { r: Reconciliation }) {
  const live = r.rows.filter((x) => x.verdict !== 'set-aside' && x.verdict !== 'not-on-file')
  const aside = r.rows.filter((x) => x.verdict === 'set-aside')
  return (
    <section className="card reconciliation">
      <div className="path-head">
        <div>
          <p className="kicker">Your workbook · {r.source}</p>
          <h2>Reconciled against what is on file</h2>
        </div>
      </div>
      <p className="lede">
        Matched <strong>{r.matched.toLocaleString()}</strong> (already on file) · new <strong>{r.new.toLocaleString()}</strong> (not on file) ·
        targets <strong>{r.targets.toLocaleString()}</strong> · conflicts <strong>{r.conflicts.toLocaleString()}</strong> (your employer or address differs from ours) ·
        removed in your file <strong>{r.removedOnFile.toLocaleString()}</strong> (on file with us; offered to set aside, never removed for you)
        {r.removedNotOnFile > 0 && <span className="muted"> · {r.removedNotOnFile.toLocaleString()} more on your removed tab are not on file, so there is nothing to do</span>}.
      </p>

      {r.bands.length > 0 && (
        <table className="diff reconciliation-bands">
          <thead><tr><th>your band</th><th>people</th><th>on file</th><th>new</th><th>conflicts</th></tr></thead>
          <tbody>
            {r.bands.map((b) => (
              <tr key={b.band}>
                <td>{b.label}</td>
                <td>{b.people.toLocaleString()}</td>
                <td>{b.matched.toLocaleString()}</td>
                <td>{b.new.toLocaleString()}</td>
                <td className={b.conflicts > 0 ? 'wins' : ''}>{b.conflicts.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {r.sheets.length > 0 && (
        <p className="muted small">
          Tabs read: {r.sheets.map((s) => `${s.name} (${s.rows.toLocaleString()})`).join(' · ')}.
        </p>
      )}

      <RowsTable title="Every row, with your band beside what Scout found" rows={live} />
      {aside.length > 0 && (
        <RowsTable title="On your removed tab, and still on file with us — set aside if you agree" rows={aside} />
      )}
    </section>
  )
}

function RowsTable({ title, rows }: { title: string; rows: ReconciliationRow[] }) {
  return (
    <details className="reconciliation-rows" open={rows.length <= 60}>
      <summary><h3>{title}</h3><span className="muted">{rows.length.toLocaleString()}</span></summary>
      <table className="diff">
        <thead><tr><th>name</th><th>company</th><th>your band</th><th>cadence</th><th>your mark</th><th>what Scout found</th><th></th></tr></thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.stagedContactId} className={`verdict-${x.verdict}`}>
              <td>
                <strong>{x.name}</strong>{x.title ? <span className="muted"> · {x.title}</span> : null}
                {x.nameNote && <span className="tag name-note">{x.nameNote}</span>}
              </td>
              <td>{x.company ?? <em className="muted">none</em>}</td>
              <td>{x.bandLabel ?? bandWord(x.band)}</td>
              <td>{x.cadenceDays != null ? `every ${x.cadenceDays} days` : ''}</td>
              <td>{[x.yourMark, x.note, x.category].filter(Boolean).join(' · ')}</td>
              <td><span className={`tag verdict ${x.verdict}`}>{VERDICT_WORDS[x.verdict] ?? x.verdict}</span> <span className="muted">{x.why}</span></td>
              <td>
                {x.correctionKind && x.personId && (
                  <Correct kind={x.correctionKind} entityId={x.personId} entityLabel={`${x.name} — ${x.why}`} compact />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  )
}

export function BenchmarkPanel({ b }: { b: BenchmarkView }) {
  return (
    <section className="card benchmark">
      <div className="path-head">
        <div>
          <p className="kicker">Your bands against Scout's · {b.source}</p>
          <h2>{b.marked.toLocaleString()} people you put in A, B or C</h2>
        </div>
      </div>
      <p className="muted small">{b.note}</p>
      <table className="diff benchmark-bands">
        <thead><tr><th>your band</th><th>people</th><th>Scout agrees</th><th>Scout lower</th><th>Scout higher</th><th>no correspondence</th><th>not on file</th></tr></thead>
        <tbody>
          {b.bands.map((x) => (
            <tr key={x.his}>
              <td>{x.label}</td>
              <td>{x.people.toLocaleString()}</td>
              <td className="wins">{x.same.toLocaleString()}</td>
              <td>{x.lower.toLocaleString()}</td>
              <td>{x.higher.toLocaleString()}</td>
              <td>{x.noCorrespondence.toLocaleString()}</td>
              <td>{x.notOnFile.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {b.disagreements.length > 0 && (
        <>
          <h3>Where you and Scout disagree most</h3>
          <table className="diff benchmark-rows">
            <thead><tr><th>name</th><th>your band</th><th>Scout's</th><th>your side</th><th>Scout's side</th></tr></thead>
            <tbody>
              {b.disagreements.map((d, i) => (
                <tr key={`${d.personId ?? d.name}-${i}`}>
                  <td><strong>{d.name}</strong>{d.company ? <span className="muted"> · {d.company}</span> : null}</td>
                  <td>{bandWord(d.hisBand)}</td>
                  <td>{d.scoutBand ? bandWord(d.scoutBand) : <em className="muted">none</em>}</td>
                  <td className="muted">{d.hisWhy}</td>
                  <td className="muted">{d.scoutWhy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <p className="muted small">Read only. Your bands are yours; Outreach keeps them. Nothing here rescored your list.</p>
    </section>
  )
}
